import React from 'react';
import { makeStyles } from '@fluentui/react-components';
import { AlyzeImage, AlyzeHubSpotForm } from '..';
import { Link } from 'react-router-dom';

export type AlyzeCtaSidebar = {
    quote?: { beforeBold?: string; bold?: string; afterBold?: string } | React.ReactNode;
    sidebarText?: string;
    authorName?: string;
    authorTitle?: string;
    avatarSrc?: string;
    avatarAlt?: string;
};

export type AlyzeCtaAction = {
    text?: string;
    href?: string;
    useHubSpotForm?: boolean;
    hubspotFormId?: string;
};

export type AlyzeCtaSectionProps = {
    backgroundSrc: string;
    backgroundAlt: string;
    titleRegular: React.ReactNode;
    titleItalic: React.ReactNode;
    subtitle?: React.ReactNode;
    action: AlyzeCtaAction;
    sidebar?: AlyzeCtaSidebar;
    showSidebar?: boolean;
    hideSidebarOnMobile?: boolean;
    useBorderRadius?: boolean; // default true, set to false for flat corners
    className?: string; // optional outer wrapper override
    overlayOpacity?: number; // optional dark overlay (0-1), e.g., 0.25 for 25% darkness
};

const useStyles = makeStyles({
    container: {
        alignSelf: 'stretch',
        position: 'relative',
        overflow: 'hidden',
        height: '713px',
        display: 'flex',
        justifyContent: 'space-between',
        gap: '20px',
        '@media (max-width: 768px)': {
            height: 'auto',
            minHeight: '800px',
            borderRadius: '0px',
            flexDirection: 'column',
            gap: '0',
        },
    },

    containerWithBorderRadius: {
        borderRadius: '40px',
        '@media (max-width: 768px)': {
            borderRadius: '0px',
        },
    },

    // Shared background
    background: {
        width: '100%',
        height: '713px',
        position: 'absolute',
        left: 0,
        top: 0,
        '& img': {
            width: '100%',
            height: '713px',
            objectFit: 'cover',
        },
        '@media (max-width: 768px)': {
            height: '800px',
            background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0) 100%)',
            backgroundBlendMode: 'multiply, normal',
            '& img': {
                height: '800px',
                objectFit: 'cover',
            },
        },
    },

    // Overlay for darkening background
    overlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, var(--overlay-opacity, 0))',
        zIndex: 1,
    },

    // Content block (left side)
    content: {
        flex: '1 1 auto',
        minWidth: '300px',
        maxWidth: '475px',
        minHeight: '713px',
        padding: '40px',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: '40px',
        zIndex: 2,
        '@media (max-width: 1024px)': {
            minHeight: '500px',
            maxWidth: '100%',
        },
        '@media (max-width: 768px)': {
            padding: '40px 20px 24px 20px',
            gap: '24px',
            alignItems: 'center',
            textAlign: 'center',
            justifyContent: 'flex-start',
            minHeight: 'auto',
        },
    },

    textBlock: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: '16px',
        '@media (max-width: 768px)': {
            alignItems: 'center',
            textAlign: 'center',
            gap: '8px',
        },
    },

    title: {
        width: '100%',
        '@media (max-width: 768px)': {
            textAlign: 'center',
        },
    },

    titleRegular: {
        color: '#F4F0E7',
        fontSize: '64px',
        fontFamily: 'Figtree',
        fontWeight: 400,
        lineHeight: '64px',
        wordWrap: 'break-word',
        '@media (max-width: 768px)': {
            fontSize: '40px',
            lineHeight: '40px',
        },
    },

    titleItalic: {
        color: '#F4F0E7',
        fontSize: '64px',
        fontFamily: 'Behind The Nineties',
        fontStyle: 'italic',
        fontWeight: 400,
        lineHeight: '64px',
        wordWrap: 'break-word',
        '@media (max-width: 768px)': {
            fontSize: '40px',
            lineHeight: '40px',
        },
    },

    subtitleRow: {
        alignSelf: 'stretch',
        paddingLeft: '4px',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: '8px',
        display: 'inline-flex',
        '@media (max-width: 768px)': {
            paddingLeft: 0,
        },
    },

    subtitleText: {
        flex: '1 1 0',
        color: '#F4F0E7',
        fontSize: '24px',
        fontFamily: 'Figtree',
        fontWeight: 400,
        lineHeight: '33.60px',
        wordWrap: 'break-word',
        '@media (max-width: 768px)': {
            fontSize: '15px',
            lineHeight: '21px',
            textAlign: 'center',
        },
    },

    actionButton: {
        paddingLeft: '32px',
        paddingRight: '32px',
        paddingTop: '20px',
        paddingBottom: '20px',
        backgroundColor: '#FFFCF5',
        borderRadius: '40px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
        display: 'inline-flex',
        cursor: 'pointer',
        border: 'none',
        textDecoration: 'none',
        transition: 'background-color 0.2s ease',
        '&:hover': {
            backgroundColor: '#f0f0f0',
        },
        '@media (max-width: 768px)': {
            paddingLeft: '24px',
            paddingRight: '24px',
            paddingTop: '16px',
            paddingBottom: '16px',
        },
    },

    actionButtonText: {
        color: '#222222',
        fontSize: '16px',
        fontFamily: 'Figtree',
        fontWeight: 700,
        lineHeight: '16px',
        wordWrap: 'break-word',
        '@media (max-width: 768px)': {
            fontSize: '12px',
            lineHeight: '12px',
        },
    },

    // Sidebar (testimonial)
    sidebar: {
        flex: '0 1 auto',
        minWidth: '280px',
        maxWidth: '386px',
        minHeight: '713px',
        paddingTop: '40px',
        paddingBottom: '40px',
        paddingRight: '40px',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '20px',
        zIndex: 2,
        '@media (max-width: 1200px)': {
            minWidth: '250px',
            maxWidth: '350px',
            minHeight: 'auto',
            paddingTop: '24px',
            paddingBottom: '24px',
            paddingRight: '24px',
        },
        '@media (max-width: 768px)': {
            maxWidth: '100%',
            width: '100%',
            padding: '24px 16px 40px 16px',
            minHeight: 'auto',
        },
    },

    testimonial: {
        width: '320px',
        padding: '40px',
        background: 'rgba(34, 34, 34, 0.30)',
        borderRadius: '32px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '24px',
        '@media (max-width: 768px)': {
            width: '100%',
            padding: '20px',
            borderRadius: '24px',
            gap: '8px',
        },
    },

    testimonialText: {
        alignSelf: 'stretch',
        color: '#FFFCF5',
        fontSize: '20px',
        fontFamily: 'Figtree',
        lineHeight: '28px',
        wordWrap: 'break-word',
        '@media (max-width: 768px)': {
            fontSize: '14px',
            lineHeight: '19.60px',
            textAlign: 'center',
        },
    },

    authorRow: {
        alignSelf: 'stretch',
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: '20px',
        '@media (max-width: 768px)': {
            flexDirection: 'column',
            gap: '0',
            alignItems: 'center',
        },
    },

    avatar: {
        width: '58px',
        height: '58px',
        borderRadius: '100px',
        objectFit: 'cover',
        overflow: 'hidden',
        '@media (max-width: 768px)': {
            width: '56px',
            height: '56px',
        },
    },

    authorInfo: {
        width: '162px',
        alignSelf: 'stretch',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: '2px',
        '@media (max-width: 768px)': {
            width: '100%',
            alignItems: 'center',
            gap: '0',
        },
    },

    authorName: {
        alignSelf: 'stretch',
        color: '#FFFCF5',
        fontSize: '16px',
        fontFamily: 'Figtree',
        fontWeight: 700,
        lineHeight: '22.40px',
        wordWrap: 'break-word',
        '@media (max-width: 768px)': {
            fontSize: '12px',
            lineHeight: '16.80px',
            textAlign: 'center',
        },
    },

    authorTitle: {
        alignSelf: 'stretch',
        color: '#FFFCF5',
        fontSize: '16px',
        fontFamily: 'Figtree',
        fontWeight: 400,
        lineHeight: '22.40px',
        wordWrap: 'break-word',
        '@media (max-width: 768px)': {
            fontSize: '12px',
            lineHeight: '16.80px',
            textAlign: 'center',
        },
    },

    sidebarHiddenOnMobile: {
        '@media (max-width: 1024px)': {
            display: 'none',
        },
    },

    // Form container (middle section between content and sidebar)
    formContainer: {
        flex: '0 1 auto',
        minWidth: '300px',
        maxWidth: '400px',
        minHeight: '713px',
        paddingTop: '40px',
        paddingBottom: '40px',
        paddingLeft: '20px',
        paddingRight: '20px',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 2,
        '@media (max-width: 1200px)': {
            minWidth: '250px',
            maxWidth: '350px',
            minHeight: 'auto',
            paddingTop: '24px',
            paddingBottom: '24px',
        },
        '@media (max-width: 768px)': {
            maxWidth: '100%',
            padding: '24px 16px',
            minHeight: 'auto',
        },
    },
});

export const AlyzeCtaSection: React.FC<AlyzeCtaSectionProps> = ({
    backgroundSrc,
    backgroundAlt,
    titleRegular,
    titleItalic,
    subtitle,
    action,
    sidebar,
    showSidebar = true,
    hideSidebarOnMobile = false,
    useBorderRadius = true,
    className,
    overlayOpacity = 0,
}) => {
    const styles = useStyles();

    // Determine content max width based on action type
    const contentMaxWidth = action.useHubSpotForm ? '450px' : '600px';

    const ActionEl = action.useHubSpotForm ? null : action.href && action.text ? (
        <Link to={action.href} className={styles.actionButton}>
            <div className={styles.actionButtonText}>{action.text}</div>
        </Link>
    ) : null;

    const renderQuote = () => {
        if (!sidebar) return null;

        // Priority 1: Use sidebarText string if provided
        if (sidebar.sidebarText) {
            return <div className={styles.testimonialText}>{sidebar.sidebarText}</div>;
        }

        // Priority 2: Use quote if provided
        if (!sidebar.quote) return null;
        if (React.isValidElement(sidebar.quote)) {
            return sidebar.quote;
        }
        const q = sidebar.quote as { beforeBold?: string; bold?: string; afterBold?: string };
        return (
            <div className={styles.testimonialText}>
                {q.beforeBold && <span>{q.beforeBold}</span>}
                {q.bold && <span style={{ fontWeight: 700 }}>{q.bold}</span>}
                {q.afterBold && <span>{q.afterBold}</span>}
            </div>
        );
    };

    return (
        <div
            className={[styles.container, useBorderRadius ? styles.containerWithBorderRadius : '', className || ''].join(' ').trim()}
            style={{ '--overlay-opacity': overlayOpacity } as React.CSSProperties}
        >
            <div className={styles.background}>
                <AlyzeImage src={backgroundSrc} alt={backgroundAlt} />
            </div>
            {overlayOpacity > 0 && <div className={styles.overlay}></div>}

            {/* Content */}
            <div className={styles.content} style={{ maxWidth: contentMaxWidth }}>
                <div className={styles.textBlock}>
                    <div className={styles.title}>
                        <span className={styles.titleRegular}>{titleRegular}</span>
                        <span className={styles.titleItalic}>{titleItalic}</span>
                    </div>
                    {subtitle && (
                        <div className={styles.subtitleRow}>
                            <div className={styles.subtitleText}>{subtitle}</div>
                        </div>
                    )}
                </div>
                {ActionEl}
            </div>

            {/* Form Container (middle section) */}
            {action.useHubSpotForm && (
                <div className={styles.formContainer}>
                    <AlyzeHubSpotForm
                        portalId="243868954"
                        formId={action.hubspotFormId ?? "da748478-db62-47ee-bd43-fa69c5da2978"}
                        region="na2"
                    />
                </div>
            )}

            {/* Sidebar (optional) */}
            {showSidebar && sidebar && (
                <div className={[styles.sidebar, hideSidebarOnMobile ? styles.sidebarHiddenOnMobile : ''].join(' ').trim()}>
                    <div className={styles.testimonial}>
                        {renderQuote()}
                        {(sidebar.authorName || sidebar.authorTitle) && (
                            <div className={styles.authorRow}>
                                {sidebar.avatarSrc ? (
                                    <AlyzeImage
                                        className={styles.avatar}
                                        src={sidebar.avatarSrc}
                                        alt={sidebar.avatarAlt || sidebar.authorName || 'Avatar'}
                                    />
                                ) : null}
                                <div className={styles.authorInfo}>
                                    {sidebar.authorName && <div className={styles.authorName}>{sidebar.authorName}</div>}
                                    {sidebar.authorTitle && <div className={styles.authorTitle}>{sidebar.authorTitle}</div>}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
